import VueStripe from '../../vendor/vue-stripe-elements'

const state = {
    /**
     * Guest Customer is a non logged-in buyer/user
     */
    guestCustomer: {
        name: {
            value: null,
            validated: false,
        },
        email: {
            value: null,
            validated: false,
        },
        wantsToRegister: false,
        confirmed: false,
        askConfirmation: false,
        validationTriggered: false,
        temporaryPasswordNotification: false,
    },

    licensesTerms: {
        agreed: false,
        validationTriggered: false,
    },

    /**
     * @type {null | string} can be either null, 'stripe' or 'paypal'
     */
    selectedPaymentMethod: null,

    stripe: {
        cardholderName: null,
        address: {
            line1: null,
            line2: null,
            country: null,
            city: null,
            zip: null,
        },
        validation: {
            cardholderName: false,
            cardNumber: false,
            cardExpiry: false,
            cardCvc: false,
            country: false,
            city: false,
            addressLine1: false,
            postZipCode: false,
        },
        validationTriggered: false,
        processing: false,
        paymentToken: null,
        paymentError: null,
        secureAuth: {
            succeeded: false,
            paymentIntentID: null,
            submitForm: false,
            error: null,
        },
    },

    paypal: {
        processing: false,
    },

    freeOrder: {
        processing: false,
    },

    formSubmit: false,

    formFieldsIds: {
        guestCustomerName: 'guestCustomerName',
        guestCustomerEmail: 'guestCustomerEmail',
        guestCustomerWantsToRegister: 'guestCustomerWantsToRegister',
        licensesTerms: 'licensesTerms',
        stripeCardholderName: 'stripeCardholderName',
        stripeCardNumber: 'stripeCardNumber',
        stripeCardExpiry: 'stripeCardExpiry',
        stripeCardCvc: 'stripeCardCvc',
        stripeCountry: 'stripeCountry',
        stripeCity: 'stripeCity',
        stripeAddressLine1: 'stripeAddressLine1',
        stripeAddressLine2: 'stripeAddressLine2',
        stripePostZipCode: 'stripePostZipCode',
    },

    containerSelector: "#content",

    firstInvalidFieldAfterSubmit: null,

    continueShoppingButtonAnimation: true,

    recaptchaWidget: null,

    recaptchaResponse: false
};

const getters = {
    customer(state, getters, rootState) {
        if (rootState.loggedInUser)
            return rootState.loggedInUser;
        else {
            return {
                name: state.guestCustomer.name.value,
                email: state.guestCustomer.email.value,
            };
        }
    },
    customerAuthenticated(state, getters, rootState) {
        return !!rootState.loggedInUser;
    },
    storeAllowCustomerLogin(state, getters, rootState) {
        return rootState.currentConfig.allowCustomerLogin;
    },
    storeAcceptPaypal(state, getters, rootState) {
        return rootState.user.muser.hasPaypal;
    },
    storeAcceptStripe(state, getters, rootState) {
        return rootState.user.muser.hasStripe && !rootState.user.muser.hasOldStripe;
    },
    storeAcceptPayment(state, getters) {
        return getters.storeAcceptPaypal || getters.storeAcceptStripe;
    },
    cartEmpty(state, getters, rootState) {
        return rootState.cart.cart.length === 0;
    },
    orderSummaryValidated(state, getters) {
        return !getters.cartEmpty;
    },
    guestCustomerDetailsValidated(state, getters) {
        if (getters.customerAuthenticated && getters.storeAllowCustomerLogin)
            return true;
        else {
            return state.guestCustomer.email.validated && state.guestCustomer.name.validated;
        }
    },
    processing(state) {
        return state.paypal.processing || state.stripe.processing;
    },
    stripePk() {
        return (ab.user.muser.hasStripe && !ab.user.muser.hasOldStripe) ? ab.user.muser.stripePk : null;
    },
    stripeRequestAddress() {
        return ab.user.muser.stripeReqAddr;
    },
    stripeValidated(state, getters) {
        return state.stripe.validation.cardholderName
            && state.stripe.validation.cardNumber
            && state.stripe.validation.cardExpiry
            && state.stripe.validation.cardCvc
            && state.stripe.validation.postZipCode
            && (
                !getters.stripeRequestAddress ? true
                    : state.stripe.validation.country
                    && state.stripe.validation.city
                    && state.stripe.validation.addressLine1
            );
    },
    stripeRequestSecureAuth() {
        return ab.requires_action;
    },
    stripePaymentIntentClientSecret() {
        return ab.payment_intent_client_secret;
    },
    checkoutDisabled(state, getters) {
        return getters.cartEmpty && !getters.stripeRequestSecureAuth;
    },
    customerIsGuest(state, getters) {
        return !getters.customerAuthenticated || !getters.storeAllowCustomerLogin;
    },
    firstInvalidField(state, getters) {
        return !getters.guestCustomerDetailsValidated
            && (!state.guestCustomer.name.validated && state.formFieldsIds.guestCustomerName
                || !state.guestCustomer.email.validated && state.formFieldsIds.guestCustomerEmail)
            || !state.licensesTerms.agreed && state.formFieldsIds.licensesTerms
            || getters.selectedPaymentMethod === 'stripe'
            && (!state.stripe.validation.cardholderName && state.formFieldsIds.stripeCardholderName
                || !state.stripe.validation.cardNumber && state.formFieldsIds.stripeCardNumber
                || !state.stripe.validation.cardExpiry && state.formFieldsIds.stripeCardExpiry
                || !state.stripe.validation.cardCvc && state.formFieldsIds.stripeCardCvc
                || (getters.stripeRequestAddress &&
                    (!state.stripe.validation.addressLine1 && state.formFieldsIds.stripeAddressLine1
                        || !state.stripe.validation.city && state.formFieldsIds.stripeCity
                        || !state.stripe.validation.country && state.formFieldsIds.stripeCountry))
                || !state.stripe.validation.postZipCode && state.formFieldsIds.stripePostZipCode)
    },
    /*
        Dummy getters for re-usability
     */

    selectedPaymentMethod: state => state.selectedPaymentMethod,
    askConfirmation: state => state.guestCustomer.askConfirmation,
    guestCustomerConfirmed: state => state.guestCustomer.confirmed,
    guestCustomerValidationTriggered: state => state.guestCustomer.validationTriggered,
    guestCustomerTemporaryPasswordNotification: state => state.guestCustomer.temporaryPasswordNotification,
    guestCustomerWantsToRegister: state => state.guestCustomer.wantsToRegister,
    paypalProcessing: state => state.paypal.processing,
    freeOrderProcessing: state => state.freeOrder.processing,
    stripeProcessing: state => state.stripe.processing,
    stripeValidationTriggered: state => state.stripe.validationTriggered,
    stripeSecureAuthSucceeded: state => state.stripe.secureAuth.succeeded,
    stripeSecureAuthPaymentIntentId: state => state.stripe.secureAuth.paymentIntentID,
    stripeSecureAuthSubmitForm: state => state.stripe.secureAuth.submitForm,
    stripeSecureAuthError: state => state.stripe.secureAuth.error,
    licensesTermsValidationTriggered: state => state.licensesTerms.validationTriggered,
    licensesTermsAgreed: state => state.licensesTerms.agreed,
    formSubmit: state => state.formSubmit,
    formFieldsIds: state => state.formFieldsIds,
    firstInvalidFieldAfterSubmit: state => state.firstInvalidFieldAfterSubmit,
    stripePaymentError: state => state.stripe.paymentError,
    stripePaymentToken: state => state.stripe.paymentToken,
    continueShoppingButtonAnimation: state => state.continueShoppingButtonAnimation,
    containerSelector: state => state.containerSelector,
    recaptchaWidget: state => state.recaptchaWidget,
    recaptchaResponse: state => state.recaptchaResponse,
    showLicenseTerms: (state, getters, rootState) => {
        const cartItems = rootState.cart.getCart()
        for (let i = 0; i < cartItems.length; i++) {
            const license = cartItems[i].license;
            const agreement = cartItems[i].item.agreement;
            console.log('agreement', agreement)
            const type = cartItems[i].type;
            const isService = ['service', 'service_quote'].includes(type);
            if (isService && agreement) {
                return true;
            } else if (!isService && license) return true;
        }
        return false;
    },
};

const mutations = {
    guestCustomerName(state, payload) {
        state.guestCustomer.name.value = payload;
    },
    guestCustomerNameValidated(state, payload) {
        state.guestCustomer.name.validated = payload;
    },
    guestCustomerEmail(state, payload) {
        state.guestCustomer.email.value = payload;
    },
    guestCustomerEmailValidated(state, payload) {
        state.guestCustomer.email.validated = payload;
    },
    guestCustomerWantsToRegister(state, payload) {
        state.guestCustomer.wantsToRegister = payload;
    },
    guestCustomerConfirmed(state, payload) {
        state.guestCustomer.confirmed = payload;
    },
    guestCustomerAskConfirmation(state, payload) {
        state.guestCustomer.askConfirmation = payload;
    },
    guestCustomerValidationTriggered(state, payload) {
        state.guestCustomer.validationTriggered = payload;
    },
    guestTemporaryPasswordNotification(state, payload) {
        state.guestCustomer.temporaryPasswordNotification = payload;
    },
    licensesTermsAgreed(state, payload) {
        state.licensesTerms.agreed = payload;
    },
    licensesTermsValidationTriggered(state, payload) {
        state.licensesTerms.validationTriggered = payload;
    },
    selectedPaymentMethod(state, payload) {
        state.selectedPaymentMethod = payload;
    },
    stripeValidation(state, payload) {
        state.stripe.validation = payload;
    },
    stripeValidationTriggered(state, payload) {
        state.stripe.validationTriggered = payload;
    },
    stripeProcessing(state, payload) {
        state.stripe.processing = payload;
    },
    stripePaymentError(state, payload) {
        state.stripe.paymentError = payload;
    },
    stripePaymentToken(state, payload) {
        state.stripe.paymentToken = payload;
    },
    stripeCardholderName(state, payload) {
        state.stripe.cardholderName = payload;
    },
    stripeAddressZip(state, payload) {
        state.stripe.address.zip = payload;
    },
    stripeAddressCountry(state, payload) {
        state.stripe.address.country = payload;
    },
    stripeAddressCity(state, payload) {
        state.stripe.address.city = payload;
    },
    stripeAddressLine1(state, payload) {
        state.stripe.address.line1 = payload;
    },
    stripeAddressLine2(state, payload) {
        state.stripe.address.line2 = payload;
    },
    stripeSecureAuthSucceeded(state, payload) {
        state.stripe.secureAuth.succeeded = payload;
    },
    stripeSecureAuthPaymentIntentId(state, payload) {
        state.stripe.secureAuth.paymentIntentID = payload;
    },
    stripeSecureAuthSubmitForm(state, payload) {
        state.stripe.secureAuth.submitForm = payload;
    },
    stripeSecureAuthError(state, payload) {
        state.stripe.secureAuth.error = payload;
    },
    paypalProcessing(state, payload) {
        state.paypal.processing = payload;
    },
    freeOrderProcessing(state, payload){
        state.freeOrder.processing = payload;
    },
    formSubmit(state, payload) {
        state.formSubmit = payload;
    },
    firstInvalidFieldAfterSubmit(state, payload) {
        state.firstInvalidFieldAfterSubmit = payload;
    },
    continueShoppingButtonAnimation(state, payload) {
        state.continueShoppingButtonAnimation = payload;
    },
    recaptchaWidget(state, payload) {
        state.recaptchaWidget = payload
    },
    recaptchaResponse(state, payload) {
        state.recaptchaResponse = payload
    }
};

const actions = {
    async handleStripeSecureAuth({getters, commit}) {
        try {
            const stripe = window.Stripe(getters.stripePk);
            const response = (await stripe.handleCardAction(getters.stripePaymentIntentClientSecret));
            if (response.error)
                throw response.error;
            commit('stripeSecureAuthSucceeded', true);
            commit('stripeSecureAuthPaymentIntentId', response.paymentIntent.id);
            commit('stripeSecureAuthSubmitForm', true);
        } catch (error) {
            commit('stripeSecureAuthSucceeded', false);
            commit('stripeSecureAuthError', error.message);
        }
    },

    selectPaymentMethod({getters, commit, dispatch}, method) {
        // Exit if processing
        if (getters.processing)
            return;

        // 0. Toggle off if the same method is selected
        if (getters.selectedPaymentMethod === method) {
            commit('selectedPaymentMethod', null);
            return;
        }

        commit('selectedPaymentMethod', method);

        if (method === 'paypal')
            dispatch('tryPayment');
        if (method === 'free')
            dispatch('tryPayment');
    },

    guestCustomerConfirm({commit, dispatch}) {
        commit('guestCustomerAskConfirmation', false);
        commit('guestCustomerConfirmed', true);
        dispatch('tryPayment');
    },

    guestCustomerDisconfirm({commit, getters}) {
        commit('guestCustomerAskConfirmation', false);
        if (getters.selectedPaymentMethod === 'paypal') commit('selectedPaymentMethod', null);
    },

    async tryPayment({getters, commit}) {
        // 0. Exit if processing
        if (getters.processing)
            return;

        // 1. Validation
        let validated = true;

        // 1a. Validation > Order Summary
        if (!getters.orderSummaryValidated) {
            // an alert message is automatically triggered if order summary is invalid
            validated = false;
        }

        // 1b. Validation > Guest Customer Details
        if ((!getters.customerAuthenticated && !getters.guestCustomerDetailsValidated) || (getters.customerIsGuest && !getters.guestCustomerDetailsValidated)) {
            if (!getters.guestCustomerValidationTriggered) commit('guestCustomerValidationTriggered', true);
            validated = false;
        }

        // 1c. Validation > Licenses Terms
        if (!getters.licensesTermsAgreed && getters.showLicenseTerms) {
            if (!getters.licensesTermsValidationTriggered) commit('licensesTermsValidationTriggered', true);
            validated = false;
        }

        // 1d. Validation > Stripe data if stripe is the payment method
        if (getters.selectedPaymentMethod === 'stripe' && !getters.stripeValidated) {
            if (!getters.stripeValidationTriggered) commit('stripeValidationTriggered', true);
            validated = false;
        }

        if (!validated) {
            // toggle off paypal selection if validation fails
            if (getters.selectedPaymentMethod === 'paypal') commit('selectedPaymentMethod', null);

            // Set first invalid field to scroll to it
            commit('firstInvalidFieldAfterSubmit', getters.firstInvalidField);
            return;
        }

        // 2. Customer Details Confirmation : If it is a guest customer and has not confirmed his details yet, prompt him for confirmation
        let confirmed = true;

        if (!getters.customerAuthenticated && !getters.guestCustomerConfirmed) {
            commit('guestCustomerAskConfirmation', true);
            confirmed = false;
        }

        if (!confirmed) return;

        // 3. Pay
        switch (getters.selectedPaymentMethod) {
            case 'paypal':
                commit('paypalProcessing', true);
                commit('formSubmit', true);
                break;
            case 'stripe':
                if (getters.recaptchaWidget && !getters.recaptchaResponse) {
                    getters.recaptchaWidget.execute();
                    return;
                }

                commit('stripeProcessing', true);

                try {
                    let type = 'card';
                    let payload = {
                        billing_details: {
                            name: state.stripe.cardholderName,
                            address: {
                                postal_code: state.stripe.address.zip,
                                ...(getters.stripeRequestAddress && {
                                    line1: state.stripe.address.line1,
                                    line2: state.stripe.address.line2 ? state.stripe.address.line2 : '',
                                    country: state.stripe.address.country,
                                    city: state.stripe.address.city,
                                })
                            },
                        }
                    };
                    const response = (await VueStripe.createPaymentMethod(type, payload));
                    if (response.error)
                        throw response.error;

                    commit('stripePaymentToken', response.paymentMethod.id);
                    commit('formSubmit', true);
                } catch (error) {
                    commit('stripePaymentError', error.message);
                    commit('stripeProcessing', false);
                }
                break;
            case 'free':
                commit('freeOrderProcessing', true);
                commit('formSubmit', true);
                break;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
